<script setup lang="ts">
import { computed } from "vue"
import type { TimeDrumGroup, TimeDrumValue } from "./index"

const props = defineProps<{
  groups: TimeDrumGroup[]
  modelValue: TimeDrumValue | null
  ariaLabel: string
  hoursLabel: string
  minutesLabel: string
}>()

const emit = defineEmits<{
  "update:modelValue": [value: TimeDrumValue | null]
}>()

const selectedGroup = computed<TimeDrumGroup | undefined>(() =>
  props.modelValue != null
    ? props.groups.find((g) => g.hour === props.modelValue!.hour)
    : undefined,
)

const visibleMinutes = computed<number[]>(() => selectedGroup.value?.minutes ?? [])

function tapHour(group: TimeDrumGroup) {
  if (props.modelValue?.hour === group.hour) return
  emit("update:modelValue", { hour: group.hour, minute: group.minutes[0] })
}

function tapMinute(minute: number) {
  if (props.modelValue == null) return
  const { hour, minute: current } = props.modelValue
  if (minute === current) {
    emit("update:modelValue", null)
  } else {
    emit("update:modelValue", { hour, minute })
  }
}

function formatHH(h: number): string {
  return String(h).padStart(2, "0")
}

function formatMM(m: number): string {
  return String(m).padStart(2, "0")
}

const headerLabel = computed<string>(() => {
  if (props.modelValue == null) return "--:--"
  return `${formatHH(props.modelValue.hour)}:${formatMM(props.modelValue.minute)}`
})
</script>

<template>
  <div
    v-if="groups.length > 0"
    data-testid="time-drum"
    class="time-drum"
    :aria-label="ariaLabel"
  >
    <div
      class="time-drum__header"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ headerLabel }}
    </div>

    <div class="time-drum__columns">
      <!-- Hours column -->
      <div
        role="group"
        :aria-label="hoursLabel"
        class="time-drum__col"
      >
        <button
          v-for="group in groups"
          :key="group.hour"
          type="button"
          data-testid="hour-item"
          :aria-pressed="modelValue != null && group.hour === modelValue.hour"
          class="time-drum__item"
          :class="{ 'time-drum__item--selected': modelValue != null && group.hour === modelValue.hour }"
          @click="tapHour(group)"
        >
          {{ formatHH(group.hour) }}
        </button>
      </div>

      <!-- Minutes column (only when an hour is selected) -->
      <div
        role="group"
        :aria-label="minutesLabel"
        class="time-drum__col"
      >
        <button
          v-for="minute in visibleMinutes"
          :key="minute"
          type="button"
          data-testid="minute-item"
          :aria-pressed="modelValue != null && minute === modelValue.minute"
          class="time-drum__item"
          :class="{ 'time-drum__item--selected': modelValue != null && minute === modelValue.minute }"
          @click="tapMinute(minute)"
        >
          {{ formatMM(minute) }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.time-drum {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.time-drum__header {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 1.25rem;
  font-weight: 600;
  text-align: center;
  color: var(--foreground);
  padding: 8px 0;
}

.time-drum__columns {
  display: flex;
  flex-direction: row;
  gap: 4px;
}

.time-drum__col {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  max-height: 240px;
  overflow-y: auto;
}

.time-drum__col::-webkit-scrollbar {
  display: none;
}

.time-drum__item {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--background);
  color: var(--foreground);
  cursor: pointer;
  font-size: 0.9375rem;
  line-height: 1;
  transition: background 0.15s, color 0.15s;
  width: 100%;
}

.time-drum__item:hover {
  background: var(--accent);
  color: var(--accent-foreground);
}

.time-drum__item:focus-visible {
  outline: 2px solid var(--ring);
  outline-offset: 2px;
}

.time-drum__item--selected {
  background: var(--primary);
  color: var(--primary-foreground);
  border-color: var(--primary);
}
</style>
